<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .questions-page
    //- Header
    .page-header
      button(@click="router.back()" type="button").back-button
        img(src="@/assets/icons/chevron-right.svg" alt="Back").back-icon
        span Help Questions
      .unanswered-badge(v-if="unansweredCount > 0")
        span {{ unansweredCount }} unanswered

    //- Filter tabs
    .filter-tabs
      button(
        v-for="filter in filters"
        :key="filter.value"
        @click="activeFilter = filter.value"
        :class="{ active: activeFilter === filter.value }"
        type="button"
      ).filter-tab {{ filter.label }}

    //- Search
    .search-row
      el-input(v-model="searchQuery" placeholder="Search questions..." clearable)
        template(#prefix)
          img(src="@/assets/icons/searchIcon.svg" alt="Search").search-icon

    //- Loading
    .loading-state(v-if="loading && !questions.length")
      .spinner
      p.loading-text Loading questions...

    //- Questions List
    .questions-list(v-if="filteredQuestions.length")
      .question-card(
        v-for="question in filteredQuestions"
        :key="question.id"
        :class="{ 'has-response': question.response }"
      )
        .question-header
          .question-from
            .asker-name {{ question.askerName || 'Anonymous' }}
            .question-email(v-if="question.askerEmail") {{ question.askerEmail }}
          .question-time {{ formatDateTime(question.createdAt) }}
        .question-text {{ question.question }}
        .response-section(v-if="question.response")
          .response-label Your Response:
          .response-text {{ question.response }}
          .response-time Answered {{ formatDateTime(question.answeredAt) }}
        .response-actions(v-if="!question.response")
          button(@click="openResponseDialog(question)" type="button").reply-button Reply
        .response-actions(v-else)
          .status-badge Answered

    //- Empty State
    .empty-state(v-else-if="!loading")
      .empty-icon 💬
      h3.empty-title {{ searchQuery ? 'No questions found' : 'No questions yet' }}
      p.empty-subtitle {{ searchQuery ? 'Try adjusting your search terms' : 'Attendees can message you with questions about your event' }}

    //- Response Dialog
    el-dialog(
      v-model="showResponseDialog"
      title="Reply to Question"
      width="500px"
      @close="resetResponseForm"
    )
      .dialog-question
        .dialog-question-label Question:
        .dialog-question-text {{ selectedQuestion?.question }}
      el-form(ref="responseFormRef" :model="responseForm" :rules="responseRules" label-position="top")
        el-form-item(label="Your Response" prop="response")
          el-input(
            v-model="responseForm.response"
            type="textarea"
            :rows="6"
            placeholder="Enter your response"
            maxlength="2000"
            show-word-limit
          )
      template(#footer)
        el-button(@click="showResponseDialog = false") Cancel
        el-button(type="primary" @click="handleSendResponse" :loading="responding") Send Response
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import dayjs from 'dayjs';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import { makeRequest } from '@/graphs/index';
import { findStickerQuestions, respondToStickerQuestion } from '@/graphs/gql';

interface StickerQuestion {
  id: string;
  stickerId: string;
  askerEntity: string;
  askerEntityId: string;
  askerEmail?: string;
  askerName?: string;
  question: string;
  response?: string;
  answeredAt?: string;
  answeredBy?: string;
  createdAt: string;
}

export default defineComponent({
  name: 'HostQuestions',
  components: { WebAppLayout },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const stickerId = String(route.params.stickerId);
    const loading = ref(false);
    const responding = ref(false);
    const showResponseDialog = ref(false);
    const searchQuery = ref('');
    const activeFilter = ref<'all' | 'unanswered' | 'answered'>('all');
    const questions = ref<StickerQuestion[]>([]);
    const selectedQuestion = ref<StickerQuestion | null>(null);
    const responseFormRef = ref();

    const responseForm = ref({
      response: '',
    });

    const responseRules = {
      response: [
        { required: true, message: 'Please enter a response', trigger: 'blur' },
        { max: 2000, message: 'Response must be 2000 characters or less', trigger: 'blur' },
      ],
    };

    const filters = [
      { label: 'All', value: 'all' },
      { label: 'Unanswered', value: 'unanswered' },
      { label: 'Answered', value: 'answered' },
    ];

    const unansweredCount = computed(() => questions.value.filter((q) => !q.response).length);

    const filteredQuestions = computed(() => {
      let list = questions.value;

      if (activeFilter.value === 'unanswered') {
        list = list.filter((q) => !q.response);
      } else if (activeFilter.value === 'answered') {
        list = list.filter((q) => q.response);
      }

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        list = list.filter(
          (q) => q.question.toLowerCase().includes(query)
            || q.askerName?.toLowerCase().includes(query)
            || q.askerEmail?.toLowerCase().includes(query),
        );
      }

      return list;
    });

    async function fetchQuestions() {
      loading.value = true;
      try {
        const response = await makeRequest(
          'query',
          findStickerQuestions,
          { stickerId },
          null,
        );
        if (response?.errors) throw new Error(response.errors[0]?.message);
        questions.value = response?.data?.findStickerQuestions || [];
      } catch (err: any) {
        console.error('Failed to load questions:', err);
        ElMessage.error('Failed to load questions');
      } finally {
        loading.value = false;
      }
    }

    function openResponseDialog(question: StickerQuestion) {
      selectedQuestion.value = question;
      showResponseDialog.value = true;
    }

    async function handleSendResponse() {
      if (!responseFormRef.value || !selectedQuestion.value) return;

      try {
        await responseFormRef.value.validate();
      } catch {
        return;
      }

      responding.value = true;
      try {
        const response = await makeRequest(
          'mutation',
          respondToStickerQuestion,
          {
            questionId: selectedQuestion.value.id,
            response: responseForm.value.response,
          },
          null,
        );

        if (response?.errors) throw new Error(response.errors[0]?.message);

        ElMessage.success('Response sent successfully');
        showResponseDialog.value = false;
        resetResponseForm();

        // Refresh the list
        await fetchQuestions();
      } catch (err: any) {
        console.error('Failed to send response:', err);
        ElMessage.error(err.message || 'Failed to send response');
      } finally {
        responding.value = false;
      }
    }

    function resetResponseForm() {
      responseForm.value.response = '';
      selectedQuestion.value = null;
      if (responseFormRef.value) {
        responseFormRef.value.clearValidate();
      }
    }

    function formatDateTime(date: string) {
      return dayjs(date).format('MMM D, YYYY h:mm A');
    }

    onMounted(fetchQuestions);

    return {
      router,
      loading,
      responding,
      showResponseDialog,
      searchQuery,
      activeFilter,
      filters,
      questions,
      filteredQuestions,
      selectedQuestion,
      responseForm,
      responseFormRef,
      responseRules,
      unansweredCount,
      openResponseDialog,
      handleSendResponse,
      resetResponseForm,
      formatDateTime,
    };
  },
});
</script>

<style lang="scss" scoped>
.questions-page {
  min-height: 100vh;
  background: #f5f5f7;
  padding-bottom: 4rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
  gap: 1rem;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  transition: opacity 0.2s;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
}

.back-icon {
  width: 1.125rem;
  height: 1.125rem;
  transform: rotate(180deg);
  opacity: 0.6;
}

.unanswered-badge {
  padding: 0.375rem 0.75rem;
  background: #ef4444;
  color: white;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.filter-tabs {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.5rem 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
}

.filter-tab {
  padding: 0.45rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 999px;
  background: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &.active {
    border-color: #0d9488;
    background: #0d9488;
    color: #fff;
  }

  &:hover:not(.active) {
    border-color: #9ca3af;
    color: #1a1a1a;
  }
}

.search-row {
  padding: 0.75rem 1.5rem;

  .search-icon {
    width: 1rem;
    height: 1rem;
    opacity: 0.5;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.5rem;
  gap: 1rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #e0e0e0;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

.questions-list {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.question-card {
  background: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &.has-response {
    border-color: #99f6e4;
    background: #f0fdf9;
  }
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.question-from {
  flex: 1;
}

.asker-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.question-email {
  font-size: 0.8rem;
  color: #6b7280;
}

.question-time {
  font-size: 0.8rem;
  color: #9ca3af;
  white-space: nowrap;
}

.question-text {
  font-size: 0.95rem;
  color: #1a1a1a;
  line-height: 1.6;
  margin-bottom: 1rem;
  white-space: pre-wrap;
}

.response-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.response-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #0d9488;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.5rem;
}

.response-text {
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 0.5rem;
  white-space: pre-wrap;
}

.response-time {
  font-size: 0.75rem;
  color: #9ca3af;
}

.response-actions {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
}

.reply-button {
  padding: 0.5rem 1rem;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.02);
  }
}

.status-badge {
  padding: 0.375rem 0.75rem;
  background: #0d9488;
  color: white;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.5rem;
  text-align: center;
}

.empty-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  opacity: 0.4;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem;
}

.empty-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
}

.dialog-question {
  margin-bottom: 1.5rem;
}

.dialog-question-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.5rem;
}

.dialog-question-text {
  font-size: 0.95rem;
  color: #1a1a1a;
  line-height: 1.6;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}
</style>
